import { useState } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { AutoComplete, Input, Typography } from "antd";

import { Profile } from "Types/Profile";

import { useProfileLookupSearch } from "./useProfileLookupSearch";

import styles from "./ProfileLookup.module.scss";

const { Text } = Typography;

const MIN_REQUIRED_CHARS = 2;

interface ProfileOption {
    value: string; // profile ID
    label: JSX.Element;
    profile: Partial<Profile>;
}

interface ProfileLookupProps {
    onProfileSelect: (profileId: string, profile: Partial<Profile>) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

function showMinCharsWarning(searchValue: string): boolean {
    if (!searchValue) {
        return false;
    } else if (searchValue.length < MIN_REQUIRED_CHARS) {
        return true;
    }
    return false;
}

export function ProfileLookup({
    onProfileSelect,
    placeholder = "Enter a Name, Phone Number, Email or External ID",
    disabled = false,
    className,
}: ProfileLookupProps): JSX.Element {
    const [searchValue, setSearchValue] = useState<string>("");
    const { handleSearchByPhoneNameEmailOrExternalId, loading, profiles } = useProfileLookupSearch();

    const handleSelect = (value: string, option: ProfileOption) => {
        onProfileSelect(value, option.profile);
        setSearchValue("");
    };

    const options: ProfileOption[] = profiles.map((profile) => ({
        value: profile.id as string,
        label: (
            <div className={styles.optionContent}>
                <Text>
                    {profile.firstName || profile.lastName
                        ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim()
                        : "Unnamed Contact"}
                </Text>
                <div className={styles.optionDetails}>
                    {profile.contactPhone && <Text type="secondary">{profile.contactPhone}</Text>}
                    {profile.email && <Text type="secondary">{profile.email}</Text>}
                    {profile.externalId && (
                        <Text type="secondary" className={styles.externalId}>
                            ID: {profile.externalId}
                        </Text>
                    )}
                </div>
            </div>
        ),
        profile,
    }));

    const notFoundContent: string | null = (() => {
        if (loading) {
            return "Loading...";
        }
        if (searchValue) {
            return "No profiles found";
        }
        return null;
    })();

    return (
        <div className={className}>
            <AutoComplete
                value={searchValue}
                options={options}
                onSearch={(searchValue) => {
                    setSearchValue(searchValue);
                    handleSearchByPhoneNameEmailOrExternalId(searchValue);
                }}
                onSelect={handleSelect}
                disabled={disabled}
                className={styles.autoComplete}
                notFoundContent={notFoundContent}
                allowClear
            >
                <Input
                    prefix={<SearchOutlined className={styles.searchIcon} />}
                    placeholder={placeholder}
                    className={styles.searchInput}
                />
            </AutoComplete>
            {showMinCharsWarning(searchValue) && (
                <Text type="secondary" className={styles.warningText}>
                    Minimum of {MIN_REQUIRED_CHARS} characters required
                </Text>
            )}
        </div>
    );
}
